import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createInventoryItem,
  deleteInventoryItem,
  fetchInventory,
  updateInventoryItem,
  passInventoryQc,
} from '../../api/inventory';
import { invalidateCache, useCachedResource } from '../../hooks/useCachedResource';
import {
  Badge,
  ConfirmDialog,
  EmptyState,
  StatusBanner,
  PageLoading,
  inputClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../components/ui';
import { ProductThumb } from '../../components/ui/ProductThumb';
import { fetchProducts } from '../../api/products';

const emptyForm = {
  id: '',
  name: '',
  asin: '',
  sku: '',
  productLink: '',
  quantityOnHand: '',
  reorderLevel: '',
  unitCost: '',
  notes: '',
  imageUrl: '',
  marketplace: 'IN',
};

const PAGE_SIZE = 50;

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function RefreshIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 7v5h-5M4 17v-5h5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 9A7 7 0 0 0 6 6.5L4 9M5.5 15A7 7 0 0 0 18 17.5l2-2.5" strokeLinecap="round" />
    </svg>
  );
}

function InventoryStat({ label, value, detail, tone = 'blue' }) {
  const tones = {
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    emerald: 'bg-emerald-500',
  };

  return (
    <article className={`${panelClassName} relative overflow-hidden p-5`}>
      <span className={`absolute inset-y-0 left-0 w-1 ${tones[tone]}`} aria-hidden="true" />
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</p>
      <div className="mt-2 flex items-end justify-between gap-3">
        <strong className="text-3xl font-semibold tracking-tight text-slate-900">{value}</strong>
        <span className="pb-1 text-xs text-slate-500">{detail}</span>
      </div>
    </article>
  );
}

function InventoryPage({ token }) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [stats, setStats] = useState({
    products: 0,
    units: 0,
    lowStock: 0,
    value: 0,
  });
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState({ type: 'loading', message: '' });
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [section, setSection] = useState('sellable');
  const [passingQcId, setPassingQcId] = useState('');

  const cacheKey = useMemo(
    () =>
      `inventory|${section}|${page}|${stockFilter}|${searchQuery.trim()}|${PAGE_SIZE}`,
    [section, page, stockFilter, searchQuery]
  );

  const fetcher = useCallback(
    () =>
      fetchInventory(token, {
        page: String(page),
        pageSize: String(PAGE_SIZE),
        stock: section === 'sellable' ? stockFilter : 'all',
        bucket: section === 'nonSellable' ? 'pending_qc' : 'sellable',
        ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
      }),
    [token, page, stockFilter, searchQuery, section]
  );

  const {
    data: inventoryData,
    loading: inventoryLoading,
    refreshing: inventoryRefreshing,
    reload: reloadInventory,
    error: inventoryError,
  } = useCachedResource(token ? cacheKey : null, fetcher, { staleTime: 90_000 });

  useEffect(() => {
    if (!inventoryData) return;
    setItems(inventoryData.items || []);
    setPagination(
      inventoryData.pagination || {
        page,
        pageSize: PAGE_SIZE,
        total: (inventoryData.items || []).length,
        totalPages: 1,
      }
    );
    setStats({
      products: inventoryData.stats?.itemCount ?? 0,
      units: inventoryData.stats?.totalUnits ?? 0,
      lowStock: inventoryData.stats?.lowStockCount ?? 0,
      value: inventoryData.stats?.inventoryValue ?? 0,
    });
  }, [inventoryData, page]);

  useEffect(() => {
    if (inventoryLoading && !items.length) {
      setStatus({ type: 'loading', message: '' });
      return;
    }
    if (inventoryError && !items.length) {
      setStatus({
        type: 'error',
        message: inventoryError.message || 'Unable to load inventory.',
      });
      return;
    }
    if (inventoryData && status.type === 'loading') {
      setStatus({ type: 'ready', message: '' });
    }
  }, [inventoryLoading, inventoryError, inventoryData, items.length, status.type]);

  const loadItems = useCallback(
    async ({ force = false } = {}) => {
      try {
        if (force) invalidateCache('inventory|');
        await reloadInventory({ force, silent: Boolean(items.length) && !force });
      } catch {
        // surfaced via status effect
      }
    },
    [reloadInventory, items.length]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery((current) => {
        if (current === searchInput) return current;
        setPage(1);
        return searchInput;
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const total = pagination.total || 0;
  const totalPages = pagination.totalPages || 0;
  const rangeStart = total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
  const rangeEnd = Math.min(pagination.page * pagination.pageSize, total);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const startEdit = (item) => {
    setForm({
      id: item.id,
      name: item.name,
      asin: item.asin || '',
      sku: item.sku || '',
      productLink: item.productLink || '',
      quantityOnHand: item.quantityOnHand,
      reorderLevel: item.reorderLevel,
      unitCost: item.unitCost,
      notes: item.notes || '',
      imageUrl: item.imageUrl || '',
      marketplace: item.marketplace || 'IN',
    });
    setIsModalOpen(true);
  };

  const resetForm = useCallback(() => {
    setForm(emptyForm);
    setIsModalOpen(false);
  }, []);

  const handleAddClick = () => {
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus({ type: 'loading', message: '' });

    const payload = {
      id: form.id || undefined,
      name: form.name,
      asin: form.asin,
      sku: form.sku,
      productLink: form.productLink,
      quantityOnHand: form.quantityOnHand,
      reorderLevel: form.reorderLevel,
      unitCost: form.unitCost,
      notes: form.notes,
    };

    try {
      if (form.id) {
        await updateInventoryItem(token, payload);
        setStatus({ type: 'success', message: 'Inventory item updated.' });
      } else {
        await createInventoryItem(token, payload);
        setStatus({ type: 'success', message: 'Inventory item created.' });
      }

      resetForm();
      await loadItems({ force: true });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete || deleting) return;
    setDeleting(true);
    try {
      await deleteInventoryItem(token, itemToDelete.id);
      setStatus({ type: 'success', message: 'Inventory item deleted.' });
      if (form.id === itemToDelete.id) {
        resetForm();
      }
      setItemToDelete(null);
      await loadItems({ force: true });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setDeleting(false);
    }
  };

  const handlePassQc = async (item) => {
    const qty = Number(item.quantityPendingQc || 0);
    if (!qty) return;
    setPassingQcId(item.id);
    try {
      await passInventoryQc(token, item.id, qty);
      setStatus({
        type: 'success',
        message: `Passed QC for ${qty} unit${qty === 1 ? '' : 's'} — moved to sellable.`,
      });
      await loadItems({ force: true });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setPassingQcId('');
    }
  };

  return (
    <section className="grid min-w-0 w-full gap-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <InventoryStat label="Products tracked" value={stats.products} detail="active SKUs" />
        <InventoryStat label="Units on hand" value={stats.units.toLocaleString('en-IN')} detail="available stock" tone="emerald" />
        <InventoryStat label="Low stock" value={stats.lowStock} detail="need attention" tone={stats.lowStock ? 'red' : 'amber'} />
        <InventoryStat label="Inventory value" value={formatCurrency(stats.value)} detail="at unit cost" />
      </div>

      <div className="flex w-fit gap-1 rounded-lg bg-slate-100 p-1" role="tablist" aria-label="Inventory section">
        {[
          { id: 'sellable', label: 'Sellable' },
          { id: 'nonSellable', label: 'Non-Sellable' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={section === tab.id}
            onClick={() => {
              setPage(1);
              setSection(tab.id);
            }}
            className={[
              'whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold transition',
              section === tab.id
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-800',
            ].join(' ')}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <StatusBanner
        status={status}
        setStatus={setStatus}
        action={
          status.type === 'error' ? (
            <button type="button" className="font-semibold hover:underline" onClick={() => loadItems({ force: true })}>
              Try again
            </button>
          ) : null
        }
      />

      {section === 'sellable' ? (
      <div className={`${panelClassName} w-full overflow-hidden`}>
        <div className="border-b border-slate-200 px-5 py-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold tracking-tight text-slate-900">Stock catalogue</h2>
              <p className="mt-1 text-sm text-slate-500">Monitor stock levels, costs, and reorder requirements.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`${secondaryButtonClassName} gap-2`}
                onClick={() => loadItems({ force: true })}
                disabled={inventoryLoading || inventoryRefreshing}
              >
                <RefreshIcon className={inventoryLoading || inventoryRefreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                Refresh
              </button>
              <button type="button" className={`${primaryButtonClassName} gap-2`} onClick={handleAddClick}>
                <span className="text-lg leading-none">+</span>
                Add item
              </button>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex w-fit gap-1 rounded-lg bg-slate-100 p-1" role="tablist" aria-label="Filter inventory">
              {[
                { id: 'all', label: 'All stock', count: stats.products },
                { id: 'low', label: 'Low stock', count: stats.lowStock },
                {
                  id: 'healthy',
                  label: 'Healthy',
                  count: Math.max(0, stats.products - stats.lowStock),
                },
              ].map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  role="tab"
                  aria-selected={stockFilter === filter.id}
                  onClick={() => {
                    setPage(1);
                    setStockFilter(filter.id);
                  }}
                  className={[
                    'whitespace-nowrap rounded-md px-3 py-2 text-xs font-semibold transition',
                    stockFilter === filter.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800',
                  ].join(' ')}
                >
                  {filter.label}
                  <span className="ml-2 text-[10px] text-slate-400">{filter.count}</span>
                </button>
              ))}
            </div>
            <label className="relative block w-full lg:max-w-sm">
              <span className="sr-only">Search inventory</span>
              <input
                type="search"
                className="crm-input pr-9"
                placeholder="Search product, ASIN, SKU or notes"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
              {searchInput ? (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs font-semibold text-slate-400 hover:text-slate-700"
                  onClick={() => {
                    setSearchInput('');
                    setSearchQuery('');
                    setPage(1);
                  }}
                  aria-label="Clear search"
                >
                  ×
                </button>
              ) : null}
            </label>
          </div>
        </div>

        {inventoryLoading && !items.length ? (
          <div className="p-5">
            <PageLoading label="Loading inventory…" />
          </div>
        ) : items.length ? (
          <div className="grid gap-0">
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[64rem] border-collapse">
              <thead>
                <tr>
                  <th className={`${tableHeadClassName} w-[36%]`}>Product</th>
                  <th className={tableHeadClassName}>Identifiers</th>
                  <th className={`${tableHeadClassName} text-center`}>Stock</th>
                  <th className={`${tableHeadClassName} text-right`}>Unit cost</th>
                  <th className={`${tableHeadClassName} text-right`}>Stock value</th>
                  <th className={`${tableHeadClassName} text-right`}>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={item.lowStock ? 'bg-amber-50/30 transition-colors hover:bg-amber-50/60' : 'transition-colors hover:bg-slate-50/70'}
                  >
                    <td className={tableCellClassName}>
                      <div className="flex items-center gap-3">
                        <ProductThumb
                          token={token}
                          item={{ title: item.name, asinIn: item.asin }}
                          marketplace="IN"
                          className="h-12 w-12"
                        />
                        <div className="min-w-0">
                          <strong className="line-clamp-2 text-sm font-semibold leading-snug text-slate-900">{item.name}</strong>
                          {item.notes ? <p className="mt-1 line-clamp-1 text-xs text-slate-500">{item.notes}</p> : null}
                        </div>
                      </div>
                    </td>
                    <td className={tableCellClassName}>
                      <div className="grid gap-1 font-mono text-xs">
                        <span className="text-slate-700">ASIN: {item.asin || '—'}</span>
                        <span className="text-slate-500">SKU: {item.sku || '—'}</span>
                      </div>
                    </td>
                    <td className={`${tableCellClassName} text-center`}>
                      <div className="grid justify-items-center gap-1.5">
                        <strong className="text-base font-semibold text-slate-900">{item.quantityOnHand}</strong>
                        <Badge tone={item.lowStock ? 'warning' : 'success'}>
                          {item.lowStock ? 'Low stock' : 'In stock'}
                        </Badge>
                      </div>
                    </td>
                    <td className={`${tableCellClassName} text-right font-semibold text-slate-900`}>
                      {formatCurrency(item.unitCost)}
                    </td>
                    <td className={`${tableCellClassName} text-right font-semibold text-slate-900`}>
                      {formatCurrency(Number(item.quantityOnHand) * Number(item.unitCost))}
                    </td>
                    <td className={`${tableCellClassName} text-right`}>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:text-blue-700"
                          onClick={() => startEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="rounded-md px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 hover:text-red-700"
                          onClick={() => setItemToDelete(item)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm">
            <p className="text-slate-600">
              Showing {rangeStart}–{rangeEnd} of {total.toLocaleString('en-IN')} item
              {total === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={page <= 1 || inventoryLoading || inventoryRefreshing}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span className="min-w-[7rem] text-center font-medium text-slate-800">
                Page {totalPages === 0 ? 0 : page} of {totalPages}
              </span>
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={totalPages === 0 || page >= totalPages || inventoryLoading || inventoryRefreshing}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </button>
            </div>
          </div>
          </div>
        ) : (
          <div className="p-5">
            <EmptyState
              title={stats.products ? 'No matching inventory' : 'No inventory items'}
              description={
                stats.products
                  ? 'Try another search term or stock filter.'
                  : 'Add products manually or receive extra stock from procurement batches.'
              }
              action={
                stats.products ? null : (
                  <button type="button" className={primaryButtonClassName} onClick={handleAddClick}>
                    Add your first item
                  </button>
                )
              }
            />
          </div>
        )}
      </div>
      ) : (
        <div className={`${panelClassName} w-full overflow-hidden`}>
          <div className="border-b border-slate-200 px-5 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold tracking-tight text-slate-900">
                  Non-sellable stock
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Returns awaiting QC. Pass QC to move units into sellable stock.
                </p>
              </div>
              <button
                type="button"
                className={`${secondaryButtonClassName} gap-2`}
                onClick={() => loadItems({ force: true })}
                disabled={inventoryLoading || inventoryRefreshing}
              >
                <RefreshIcon
                  className={
                    inventoryLoading || inventoryRefreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'
                  }
                />
                Refresh
              </button>
            </div>
          </div>

          {inventoryLoading && !items.length ? (
            <div className="p-5">
              <PageLoading label="Loading pending QC stock…" />
            </div>
          ) : items.length ? (
            <div className="grid gap-3 p-5">
              <div className="overflow-x-auto rounded-[10px] border border-slate-200">
                <table className="w-full min-w-[48rem] border-collapse">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className={tableHeadClassName}>Product</th>
                      <th className={tableHeadClassName}>ASIN</th>
                      <th className={tableHeadClassName}>SKU</th>
                      <th className={`${tableHeadClassName} text-right`}>Pending QC</th>
                      <th className={`${tableHeadClassName} text-right`}>Unit cost</th>
                      <th className={`${tableHeadClassName} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} className="border-t border-slate-100">
                        <td className={tableCellClassName}>
                          <div className="flex items-center gap-3">
                            <ProductThumb
                              token={token}
                              item={{ title: item.name, asinIn: item.asin }}
                              marketplace="IN"
                              className="h-12 w-12"
                            />
                            <span className="text-sm font-medium text-slate-900">{item.name}</span>
                          </div>
                        </td>
                        <td className={tableCellClassName}>
                          <span className="font-mono text-xs text-slate-700">{item.asin || '—'}</span>
                        </td>
                        <td className={tableCellClassName}>
                          <span className="font-mono text-xs text-slate-700">{item.sku || '—'}</span>
                        </td>
                        <td className={`${tableCellClassName} text-right`}>
                          <span className="text-sm font-semibold text-amber-800">
                            {Number(item.quantityPendingQc || 0).toLocaleString('en-IN')}
                          </span>
                        </td>
                        <td className={`${tableCellClassName} text-right`}>
                          {formatCurrency(item.unitCost)}
                        </td>
                        <td className={`${tableCellClassName} text-right`}>
                          <button
                            type="button"
                            className={primaryButtonClassName}
                            disabled={
                              passingQcId === item.id || Number(item.quantityPendingQc || 0) <= 0
                            }
                            onClick={() => handlePassQc(item)}
                          >
                            {passingQcId === item.id ? 'Passing…' : 'Pass QC'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <p className="text-slate-600">
                  Showing {rangeStart}–{rangeEnd} of {total.toLocaleString('en-IN')} item
                  {total === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={secondaryButtonClassName}
                    disabled={page <= 1 || inventoryLoading || inventoryRefreshing}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    Previous
                  </button>
                  <span className="min-w-[7rem] text-center font-medium text-slate-800">
                    Page {totalPages === 0 ? 0 : page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className={secondaryButtonClassName}
                    disabled={
                      totalPages === 0 ||
                      page >= totalPages ||
                      inventoryLoading ||
                      inventoryRefreshing
                    }
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-5">
              <EmptyState
                title="No pending QC stock"
                description="When Fulfillment marks an order Non-sellable with Return by buyer, quantity appears here until QC is passed."
              />
            </div>
          )}
        </div>
      )}

      <InventoryModal
        isOpen={isModalOpen}
        onClose={resetForm}
        form={form}
        onChange={handleChange}
        onPickProduct={(product) => {
          if (!product) {
            setForm((current) => ({
              ...current,
              name: '',
              asin: '',
              sku: '',
              imageUrl: '',
              marketplace: 'IN',
            }));
            return;
          }
          setForm((current) => ({
            ...current,
            name: product.title || current.name,
            asin: product.asin || current.asin || '',
            sku: product.sellerSku || current.sku || '',
            imageUrl: product.imageUrl || '',
            marketplace: product.marketplace || 'IN',
          }));
        }}
        onSubmit={handleSubmit}
        saving={saving}
        token={token}
      />
      <ConfirmDialog
        open={Boolean(itemToDelete)}
        title="Do you want to delete?"
        message={
          itemToDelete
            ? `${itemToDelete.name} will be permanently removed from your inventory.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirming={deleting}
        confirmingLabel="Deleting…"
        onConfirm={handleDelete}
        onCancel={() => {
          if (!deleting) setItemToDelete(null);
        }}
      />
    </section>
  );
}

function InventoryModal({
  isOpen,
  onClose,
  form,
  onChange,
  onPickProduct,
  onSubmit,
  saving,
  token,
}) {
  const searchRef = useRef(null);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) {
        if (searchOpen) {
          setSearchOpen(false);
          return;
        }
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, saving, searchOpen]);

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
    } else {
      setCatalogQuery('');
      setSuggestions([]);
      setSearchLoading(false);
      setSearchOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !token) return undefined;

    const term = catalogQuery.trim();
    if (term.length < 2) {
      setSuggestions([]);
      setSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setSearchLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const payload = await fetchProducts(token, {
          search: term,
          page: '1',
          pageSize: '20',
        });
        if (cancelled) return;
        setSuggestions(payload.items || []);
        setSearchOpen(true);
      } catch (error) {
        if (!cancelled) {
          console.error('Inventory product search failed:', error);
          setSuggestions([]);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 280);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isOpen, token, catalogQuery]);

  if (!isOpen) return null;

  const previewItem = {
    title: form.name,
    asinIn: form.asin,
    asinCom: form.asin,
    imageUrl: form.imageUrl || null,
  };
  const hasSelection = Boolean(form.name || form.asin || form.sku || form.imageUrl);
  const term = catalogQuery.trim();
  const showResults = searchOpen && (term.length >= 2 || searchLoading);

  const pickProduct = (product) => {
    onPickProduct?.(product);
    setCatalogQuery('');
    setSuggestions([]);
    setSearchOpen(false);
  };

  const clearSelection = () => {
    onPickProduct?.(null);
  };

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
      role="presentation"
      onClick={() => !saving && onClose()}
    >
      <div
        className="relative flex max-h-[min(92vh,44rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_24px_64px_-28px_rgba(15,23,42,0.45)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inventory-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="h-1 shrink-0 bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400" />

        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200/80 px-6 py-4">
          <div className="min-w-0">
            <h2
              id="inventory-modal-title"
              className="text-lg font-semibold tracking-tight text-slate-900"
            >
              {form.id ? 'Edit inventory item' : 'Add inventory item'}
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Find a listing, then set stock and cost.
            </p>
          </div>
          <button
            type="button"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            onClick={onClose}
            disabled={saving}
            aria-label="Close inventory form"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {/* Catalog search */}
            <section className="space-y-3">
              <div className="flex items-end justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Catalog
                  </p>
                  <p className="text-sm font-medium text-slate-800">Search products</p>
                </div>
                {hasSelection ? (
                  <button
                    type="button"
                    className="text-xs font-semibold text-slate-500 hover:text-red-600"
                    onClick={clearSelection}
                  >
                    Clear selection
                  </button>
                ) : null}
              </div>

              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  ref={searchRef}
                  type="search"
                  className={`${inputClassName} min-h-12 rounded-xl border-slate-200 bg-slate-50/80 pl-10 pr-10 text-[15px] hover:border-slate-300 focus:bg-white`}
                  value={catalogQuery}
                  onChange={(event) => {
                    setCatalogQuery(event.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  placeholder="Name, ASIN, or SKU…"
                  autoComplete="off"
                />
                {searchLoading ? (
                  <span className="absolute inset-y-0 right-3 flex items-center text-xs font-medium text-slate-400">
                    …
                  </span>
                ) : catalogQuery ? (
                  <button
                    type="button"
                    className="absolute inset-y-0 right-2 my-auto grid h-7 w-7 place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    onClick={() => {
                      setCatalogQuery('');
                      setSuggestions([]);
                      searchRef.current?.focus();
                    }}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                ) : null}
              </div>

              {showResults ? (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  {term.length < 2 ? (
                    <p className="px-4 py-3 text-center text-sm text-slate-400">
                      Type at least 2 characters
                    </p>
                  ) : searchLoading && suggestions.length === 0 ? (
                    <p className="px-4 py-3 text-center text-sm text-slate-400">Searching listings…</p>
                  ) : suggestions.length === 0 ? (
                    <p className="px-4 py-3 text-center text-sm text-slate-400">
                      No matches — enter details manually below
                    </p>
                  ) : (
                    <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto">
                      {suggestions.map((product) => (
                        <li key={product.id}>
                          <button
                            type="button"
                            className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-sky-50/80"
                            onClick={() => pickProduct(product)}
                          >
                            <ProductThumb
                              item={{
                                asinIn: product.asin,
                                asinCom: product.asin,
                                imageUrl: product.imageUrl,
                                title: product.title,
                              }}
                              marketplace={product.marketplace || 'IN'}
                              token={token}
                              className="h-12 w-12 shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <p
                                className="line-clamp-2 text-sm font-semibold leading-snug text-slate-900"
                                title={product.title}
                              >
                                {product.title || 'Untitled product'}
                              </p>
                              <p className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-slate-500">
                                {product.marketplace ? (
                                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold uppercase text-slate-600">
                                    {product.marketplace}
                                  </span>
                                ) : null}
                                {product.asin ? (
                                  <span className="rounded bg-slate-50 px-1.5 py-0.5 font-mono">
                                    {product.asin}
                                  </span>
                                ) : null}
                                {product.sellerSku ? (
                                  <span className="max-w-[12rem] truncate rounded bg-slate-50 px-1.5 py-0.5 font-mono">
                                    {product.sellerSku}
                                  </span>
                                ) : null}
                              </p>
                            </div>
                            <span className="shrink-0 text-xs font-semibold text-sky-700">Select</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-400">
                  Results show image + title. Or skip search and type details below.
                </p>
              )}

              {hasSelection ? (
                <div className="flex items-start gap-3 rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white px-3.5 py-3">
                  <ProductThumb
                    item={previewItem}
                    marketplace={form.marketplace || 'IN'}
                    token={token}
                    className="h-16 w-16 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700/80">
                      Selected
                    </p>
                    <p className="mt-0.5 text-sm font-semibold leading-snug text-slate-900" title={form.name}>
                      {form.name || 'Untitled'}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {form.asin ? (
                        <span className="rounded-md bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-600 ring-1 ring-slate-200">
                          ASIN {form.asin}
                        </span>
                      ) : null}
                      {form.sku ? (
                        <span className="rounded-md bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-600 ring-1 ring-slate-200">
                          SKU {form.sku}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </section>

            <div className="border-t border-slate-100" />

            {/* Identity fields */}
            <section className="space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Product details
                </p>
                <p className="text-sm text-slate-500">Filled from catalog — edit if needed.</p>
              </div>

              <label className="grid gap-1.5">
                <span className="text-xs font-semibold text-slate-700">
                  Product name <span className="text-red-500">*</span>
                </span>
                <input
                  className={`${inputClassName} min-h-11 rounded-xl bg-white hover:border-slate-300`}
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="e.g. Tampax Pearl Tampons, Ultra Absorbency"
                  required
                  autoComplete="off"
                />
              </label>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="grid gap-1.5">
                  <span className="text-xs font-semibold text-slate-700">ASIN</span>
                  <input
                    className={`${inputClassName} min-h-11 rounded-xl bg-white font-mono text-sm hover:border-slate-300`}
                    name="asin"
                    value={form.asin}
                    onChange={onChange}
                    placeholder="B07NQ5D435"
                    autoComplete="off"
                  />
                </label>
                <label className="grid gap-1.5">
                  <span className="text-xs font-semibold text-slate-700">SKU</span>
                  <input
                    className={`${inputClassName} min-h-11 rounded-xl bg-white font-mono text-sm hover:border-slate-300`}
                    name="sku"
                    value={form.sku}
                    onChange={onChange}
                    placeholder="TIS-N-B07NQ5D435"
                    autoComplete="off"
                  />
                </label>
              </div>
            </section>

            <div className="border-t border-slate-100" />

            {/* Stock */}
            <section className="space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Stock & cost
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="grid gap-1.5">
                  <span className="text-xs font-semibold text-slate-700">Quantity on hand</span>
                  <input
                    className={`${inputClassName} min-h-11 rounded-xl bg-white hover:border-slate-300`}
                    type="number"
                    min="0"
                    name="quantityOnHand"
                    value={form.quantityOnHand}
                    onChange={onChange}
                    placeholder="0"
                  />
                </label>
                <label className="grid gap-1.5">
                  <span className="text-xs font-semibold text-slate-700">Unit cost</span>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span className="text-sm font-semibold text-slate-500">$</span>
                    </div>
                    <input
                      className={`${inputClassName} min-h-11 rounded-xl bg-white pl-7 hover:border-slate-300`}
                      type="number"
                      step="0.01"
                      min="0"
                      name="unitCost"
                      value={form.unitCost}
                      onChange={onChange}
                      placeholder="0.00"
                    />
                  </div>
                </label>
              </div>

              <label className="grid gap-1.5">
                <span className="text-xs font-semibold text-slate-700">Reorder level</span>
                <input
                  className={`${inputClassName} min-h-11 rounded-xl bg-white hover:border-slate-300`}
                  type="number"
                  min="0"
                  name="reorderLevel"
                  value={form.reorderLevel}
                  onChange={onChange}
                  placeholder="Alert when stock reaches this quantity"
                />
                <span className="text-xs text-slate-400">Used to flag low-stock items.</span>
              </label>

              <label className="grid gap-1.5">
                <span className="text-xs font-semibold text-slate-700">Supplier or product link</span>
                <input
                  className={`${inputClassName} min-h-11 rounded-xl bg-white hover:border-slate-300`}
                  name="productLink"
                  value={form.productLink}
                  onChange={onChange}
                  placeholder="Amazon, Walmart, or supplier URL"
                />
              </label>

              <label className="grid gap-1.5">
                <span className="text-xs font-semibold text-slate-700">Notes</span>
                <textarea
                  className={`${inputClassName} min-h-[5.5rem] resize-y rounded-xl bg-white py-3 hover:border-slate-300`}
                  name="notes"
                  value={form.notes}
                  onChange={onChange}
                  placeholder="Supplier notes, pack size, etc."
                />
              </label>
            </section>
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 bg-slate-50/90 px-6 py-3.5">
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className={primaryButtonClassName} disabled={saving}>
              {saving ? 'Saving…' : form.id ? 'Save changes' : 'Add item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default InventoryPage;
